import type { LocationData } from '@ikegps/core-api';
import { override, makeObservable } from 'mobx';
import { T } from '../../i18n';
import { DistanceInputState } from './distance.input.state';
import { InputState } from './input.state';
import { LocationFieldAngleInputState, LocationFieldInputState } from './LocationField.input.state';
import { NumberInputState } from './number.input.state';

export class LocationInputState extends InputState<LocationData> {
	static readonly DEFAULT_PRECISION: number = 7;
	static readonly DEFAULT_MINIMUM_INPUT_PRECISION: number = 6;
	static readonly DEFAULT_ALTITUDE_PRECISION: number = 2;

	precision: number;
	minimumInputPrecision: number;
	hideZero: boolean = false;

	latitude: LocationFieldInputState;
	longitude: LocationFieldInputState;
	altitude: DistanceInputState;

	@override override get value(): LocationData | null {
		if (this.latitude.value == null || this.longitude.value == null) {
			return null;
		}

		return {
			latitude: this.latitude.value,
			longitude: this.longitude.value,
			altitude: this.altitude.value,
		};
	}

	static equal(
		value1: LocationData,
		value2: LocationData,
		precision: number = LocationInputState.DEFAULT_PRECISION
	): boolean {
		if (value1 === value2) {
			return true;
		}

		if (value1 == null || value2 == null) {
			return false;
		}

		return (
			NumberInputState.equal(value1.latitude, value2.latitude, precision) &&
			NumberInputState.equal(value1.longitude, value2.longitude, precision) &&
			NumberInputState.equal(value1.altitude, value2.altitude, LocationInputState.DEFAULT_ALTITUDE_PRECISION)
		);
	}

	constructor(
		label: string,
		precision: number = LocationInputState.DEFAULT_PRECISION,
		minimumInputPrecision: number = LocationInputState.DEFAULT_MINIMUM_INPUT_PRECISION
	) {
		super(label);

		makeObservable(this);

		this.precision = precision;
		this.minimumInputPrecision = minimumInputPrecision;
		this.isLabelShown = false;

		this.latitude = new LocationFieldAngleInputState(T.LAT, precision, minimumInputPrecision, 90);
		this.longitude = new LocationFieldAngleInputState(T.LNG, precision, minimumInputPrecision, 180);
		this.altitude = new DistanceInputState(T.FIELD_LOCATION_ALT);
	}

	@override override setValue(val: LocationData) {
		if (val == null) {
			this.latitude.setValue(null);
			this.longitude.setValue(null);
			this.altitude.setValue(null);
			return;
		}

		this.latitude.setValue(val.latitude);
		this.longitude.setValue(val.longitude);
		this.altitude.setValue(val.altitude);
	}

	override hasError() {
		return super.hasError() || this.latitude.hasError() || this.longitude.hasError() || this.altitude.hasError();
	}

	@override override get error(): string {
		// Don't show an error message if one is already being shown
		if (this.latitude.hasError() || this.longitude.hasError() || this.altitude.hasError()) {
			return null;
		}

		if (this.latitude.value != null && this.longitude.value == null) {
			return T.LONGITUDE_MISSING;
		}

		if (this.latitude.value == null) {
			return T.LATITUDE_MISSING;
		}

		return this._error;
	}

	override set error(msg: string) {
		this._error = msg || '';
	}

	override destroy() {
		this.latitude.destroy();
		this.longitude.destroy();
		this.altitude.destroy();
		super.destroy();
	}
}
